import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router'
import { FiSearch, FiUser, FiUsers, FiList, FiMapPin } from 'react-icons/fi'
import { Box, Icon, Input, Portal, Spinner, Stack, Text, InputGroup } from '@/components/lib'
import api from '../../api'
import useStore from '../../store'
import { searchPages } from '../../util/trialPages'

const TYPE_CONFIG = {
  participant: { icon: FiUser, label: 'Participant' },
  group: { icon: FiUsers, label: 'Group' },
  stage: { icon: FiList, label: 'Stage' },
  site: { icon: FiMapPin, label: 'Site' },
  page: { icon: null, label: 'Page' } // icon comes from result.icon
}

const TYPE_ORDER = ['page', 'participant', 'group', 'stage', 'site']

function resultUrl (trial, result) {
  switch (result.type) {
    case 'participant':
      return `/trials/${trial._id}/participants/${result._id}`
    case 'group':
      return null // handled via navigate + store update
    case 'stage':
      return `/trials/${trial._id}/stages/${result._id}`
    case 'site':
      return `/trials/${trial._id}/sites/${result._id}`
    case 'page':
      return result.url
    default:
      return null
  }
}

export default function TrialSearch () {
  const navigate = useNavigate()
  const { user, trial, setParticipantsTableFilters } = useStore()
  const [query, setQuery] = useState('')
  const [apiResults, setApiResults] = useState([])
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)
  const [focused, setFocused] = useState(0)
  const [dropdownStyle, setDropdownStyle] = useState({})
  const debounceRef = useRef(null)
  const containerRef = useRef(null)
  const inputRef = useRef(null)
  const dropdownRef = useRef(null)

  // Merge page results (front-end) with API results
  const pageResults = query.trim() ? searchPages(trial?._id, query, user, trial) : []
  const results = [...pageResults, ...apiResults]

  // Group results by type in display order
  const grouped = TYPE_ORDER.reduce((acc, type) => {
    const items = results.filter(r => r.type === type)
    if (items.length) acc.push({ type, items })
    return acc
  }, [])

  const flatResults = grouped.flatMap(g => g.items)

  const doSearch = useCallback((q) => {
    if (!q.trim() || !trial?._id) {
      setApiResults([])
      setLoading(false)
      return
    }
    setLoading(true)
    api.trials.search(trial._id, q,
      data => {
        setApiResults(data.results || [])
        setLoading(false)
        if (containerRef.current) {
          const rect = containerRef.current.getBoundingClientRect()
          setDropdownStyle({
            position: 'fixed',
            top: rect.bottom + 6,
            left: rect.left,
            width: Math.max(rect.width, 280)
          })
        }
        setOpen(true)
      },
      () => setLoading(false)
    )
  }, [trial?._id])

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    if (!query.trim()) {
      setApiResults([])
      setOpen(false)
      return
    }
    // Show page results immediately, API results after debounce
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect()
      setDropdownStyle({
        position: 'fixed',
        top: rect.bottom + 6,
        left: rect.left,
        width: Math.max(rect.width, 280)
      })
    }
    setOpen(true)
    debounceRef.current = setTimeout(() => doSearch(query), 300)
    return () => clearTimeout(debounceRef.current)
  }, [query, doSearch])

  // Close on outside click (container or portalled dropdown)
  useEffect(() => {
    function handler (e) {
      const inContainer = containerRef.current?.contains(e.target)
      const inDropdown = dropdownRef.current?.contains(e.target)
      if (!inContainer && !inDropdown) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  function handleSelect (result) {
    setOpen(false)
    setQuery('')
    if (result.type === 'group') {
      setParticipantsTableFilters({ groupIncludeIds: [result._id], page: 1 })
      navigate(`/trials/${trial._id}/participants`)
    } else {
      const url = resultUrl(trial, result)
      if (url) navigate(url)
    }
  }

  function handleKeyDown (e) {
    if (!open || !flatResults.length) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setFocused(f => Math.min(f + 1, flatResults.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setFocused(f => Math.max(f - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (flatResults[focused]) handleSelect(flatResults[focused])
    } else if (e.key === 'Escape') {
      setOpen(false)
    }
  }

  useEffect(() => { setFocused(0) }, [query])

  if (!trial?._id) return null

  return (
    <Box ref={containerRef} position='relative' w={{ base: '160px', md: '240px' }}>
      <Stack direction='row' alignItems='center'>
        <InputGroup startElement={<Icon as={FiSearch} />} endElement={loading ? <Spinner size='xs' color='fg.muted' /> : null}>
          <Input
            ref={inputRef}
            size='sm'
            variant='subtle'
            placeholder='Find something...'
            value={query}
            onChange={e => setQuery(e.target.value)}
            onFocus={() => { if (results.length) setOpen(true) }}
            onKeyDown={handleKeyDown}
          />
        </InputGroup>
      </Stack>

      {open && (
        <Portal>
          <Box
            ref={dropdownRef}
            style={dropdownStyle}
            zIndex={200}
            bg='bg.surface'
            borderWidth='1px'
            borderColor='border.default'
            borderRadius='md'
            boxShadow='md'
            overflow='hidden'
          >
            {grouped.length === 0 && !loading && (
              <Box px={3} py={2}>
                <Text fontSize='sm' color='fg.muted'>No results found</Text>
              </Box>
            )}
            {grouped.map(({ type, items }) => {
              const config = TYPE_CONFIG[type]
              return (
                <Box key={type}>
                  <Box px={3} py={1} bg='bg.subtle'>
                    <Text fontSize='xs' fontWeight='semibold' color='fg.muted' textTransform='uppercase' letterSpacing='wider'>
                      {config.label}{items.length !== 1 ? 's' : ''}
                    </Text>
                  </Box>
                  {items.map((result) => {
                    const globalIdx = flatResults.indexOf(result)
                    const isFocused = globalIdx === focused
                    const icon = type === 'page' ? result.icon : config.icon
                    return (
                      <Box
                        key={result._id}
                        px={3}
                        py={2}
                        cursor='pointer'
                        bg={isFocused ? 'bg.subtle' : 'transparent'}
                        _hover={{ bg: 'bg.subtle' }}
                        onClick={() => handleSelect(result)}
                        onMouseEnter={() => setFocused(globalIdx)}
                      >
                        <Stack direction='row' alignItems='center' gap={2}>
                          <Icon as={icon} boxSize={3.5} color='fg.muted' flexShrink={0} />
                          <Box overflow='hidden'>
                            <Stack direction='row' alignItems='baseline' gap={1.5}>
                              <Text fontSize='sm' fontWeight='medium' truncate>{result.label}</Text>
                              {result.sublabel && (
                                <Text fontSize='xs' color='fg.muted' truncate>{result.sublabel}</Text>
                              )}
                            </Stack>
                            {result.detail && (
                              <Text fontSize='xs' color='fg.muted' truncate>{result.detail}</Text>
                            )}
                          </Box>
                        </Stack>
                      </Box>
                    )
                  })}
                </Box>
              )
            })}
          </Box>
        </Portal>
      )}
    </Box>
  )
}
